/**
 * 🎬 Advanced Loading Screen
 * 
 * Écran de chargement avec indicateur de qualité réseau
 * et progression adaptative - Version simplifiée
 */

import { memo, useEffect, useState, useMemo } from 'react';
import { Loader2, Wifi, WifiOff } from 'lucide-react';
import { usePerformance } from '../../context/PerformanceContext';

// ============================================================================
// Connection Status Indicator
// ============================================================================

interface ConnectionStatusProps {
  showLabel?: boolean;
}

export const ConnectionStatusIndicator = memo<ConnectionStatusProps>(({ showLabel = true }) => {
  const { connectionQuality, isOnline } = usePerformance();

  const getStatusInfo = () => {
    if (!isOnline) return { icon: WifiOff, color: 'text-red-500', label: 'Hors ligne' };
    
    switch (connectionQuality) {
      case 'excellent':
        return { icon: Wifi, color: 'text-green-500', label: 'Connexion excellente' };
      case 'good':
        return { icon: Wifi, color: 'text-green-400', label: 'Bonne connexion' };
      case 'moderate':
        return { icon: Wifi, color: 'text-yellow-500', label: 'Connexion modérée' };
      case 'poor':
        return { icon: Wifi, color: 'text-orange-500', label: 'Connexion lente' };
      default:
        return { icon: Wifi, color: 'text-gray-400', label: 'Vérification...' };
    }
  };

  const { icon: Icon, color, label } = getStatusInfo();

  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${color}`} />
      {showLabel && <span className="text-sm text-gray-500">{label}</span>}
    </div>
  );
});

ConnectionStatusIndicator.displayName = 'ConnectionStatusIndicator';

// ============================================================================
// Progress Bar
// ============================================================================

interface ProgressBarProps {
  progress: number;
  showPercentage?: boolean;
}

export const ProgressBar = memo<ProgressBarProps>(({ progress, showPercentage = true }) => {
  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className="w-full max-w-xs">
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
      {showPercentage && (
        <p className="text-center text-sm text-gray-500 mt-2">{Math.round(clampedProgress)}%</p>
      )}
    </div>
  );
});

ProgressBar.displayName = 'ProgressBar';

// ============================================================================
// Main Loading Screen
// ============================================================================

interface AdvancedLoadingScreenProps {
  message?: string;
  showConnectionStatus?: boolean;
  showProgress?: boolean;
  minimumDuration?: number;
  onComplete?: () => void;
  theme?: 'light' | 'brand';
}

export const AdvancedLoadingScreen = memo<AdvancedLoadingScreenProps>(({
  message,
  showConnectionStatus = true,
  showProgress = true,
  minimumDuration = 600,
  onComplete,
  theme = 'light',
}) => {
  const { isInitialLoading, loadingProgress, isSlowConnection } = usePerformance();
  const [shouldShow, setShouldShow] = useState(true);
  const [startTime] = useState(Date.now());

  // Gérer la durée minimum d'affichage
  useEffect(() => {
    if (!isInitialLoading) {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minimumDuration - elapsed);
      
      const timer = setTimeout(() => {
        setShouldShow(false);
        onComplete?.();
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [isInitialLoading, minimumDuration, startTime, onComplete]);

  // Message adaptatif
  const displayMessage = useMemo(() => {
    if (message) return message;
    if (isSlowConnection) return 'Chargement optimisé...';
    if (loadingProgress >= 90) return 'Finalisation...';
    return 'Chargement...';
  }, [message, isSlowConnection, loadingProgress]);

  if (!shouldShow) return null;

  const bgClass = theme === 'brand' 
    ? 'bg-gradient-to-br from-primary/5 via-white to-accent/5' 
    : 'bg-white';

  return (
    <div className={`fixed inset-0 z-50 ${bgClass} flex flex-col items-center justify-center`}>
      <div className="flex flex-col items-center gap-6 p-8">
        {/* Spinner */}
        <div className="relative">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>

        {/* Message */}
        <p className="text-lg font-medium text-gray-700">{displayMessage}</p>

        {/* Progress bar */}
        {showProgress && <ProgressBar progress={loadingProgress} />}

        {/* Connection status */}
        {showConnectionStatus && (
          <div className="mt-2">
            <ConnectionStatusIndicator />
          </div>
        )}
      </div>
    </div>
  );
});

AdvancedLoadingScreen.displayName = 'AdvancedLoadingScreen';

// ============================================================================
// Inline Loading Indicator
// ============================================================================

interface InlineLoadingIndicatorProps {
  size?: 'sm' | 'md' | 'lg';
  message?: string;
}

export const InlineLoadingIndicator = memo<InlineLoadingIndicatorProps>(({ 
  size = 'md', 
  message 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <div className="flex items-center gap-3">
      <Loader2 className={`${sizeClasses[size]} text-primary animate-spin`} />
      {message && <span className="text-gray-600">{message}</span>}
    </div>
  );
});

InlineLoadingIndicator.displayName = 'InlineLoadingIndicator';

export default AdvancedLoadingScreen;
